import { useState, useCallback, useEffect } from 'react';

// One shared datalist backs every row's bank field, so the suggestions are
// fetched once for the list rather than per invite.
const BANK_LIST_ID = 'hq-bank-options';

const REGION_FLAGS = {
  DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹', NL: '🇳🇱', PT: '🇵🇹',
  UK: '🇬🇧', ES: '🇪🇸', SE: '🇸🇪', PL: '🇵🇱',
  US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽', BR: '🇧🇷', CO: '🇨🇴', AR: '🇦🇷',
  AU: '🇦🇺', JP: '🇯🇵', SG: '🇸🇬', AE: '🇦🇪', IN: '🇮🇳',
};

const TYPE_LABEL = {
  product_invitation: { label: 'Purchase Invite', color: '#a78bfa', bg: 'rgba(167,139,250,0.15)' },
  vine:               { label: 'Vine',             color: '#34d399', bg: 'rgba(52,211,153,0.15)'  },
  early_reviewer:     { label: 'Early Reviewer',   color: '#fbbf24', bg: 'rgba(251,191,36,0.15)'  },
};

const OUTCOMES = [
  { key: 'purchased', label: 'Purchased', desc: 'Order went through',            color: '#34d399', bg: 'rgba(52,211,153,0.18)'  },
  { key: 'cancelled', label: 'Cancelled', desc: 'Order placed, then cancelled',  color: '#f87171', bg: 'rgba(248,113,113,0.18)' },
  { key: 'suspended', label: 'Suspended', desc: 'Account suspended on the order', color: '#fb923c', bg: 'rgba(251,146,60,0.18)'  },
  { key: 'skipped',   label: 'Skipped',   desc: 'Never attempted',               color: '#64748b', bg: 'rgba(100,116,139,0.18)' },
];

// Only these two put a real order on a doorstep, so only these carry an address
// and a payment method.
const ADDRESS_OUTCOMES = ['purchased', 'cancelled'];
const EMPTY_ADDRESS    = { line1: '', line2: '', postcode: '' };

function addressOf(log) {
  return { ...EMPTY_ADDRESS, ...(log?.address || {}) };
}

// The editable form is the address plus the bank, which is stored beside the
// address rather than inside it.
function draftOf(log) {
  return { ...addressOf(log), bank: log?.bank || '' };
}

function sameDraft(a, b) {
  return a.line1.trim() === b.line1.trim()
      && a.line2.trim() === b.line2.trim()
      && a.postcode.trim() === b.postcode.trim()
      && a.bank.trim() === b.bank.trim();
}

// An address is usable for the tracker once it has a street line and a postcode;
// line 2 is genuinely optional.
function addressComplete(a) {
  return !!(a.line1.trim() && a.postcode.trim());
}

const WINDOW_MS = 72 * 60 * 60 * 1000;

function timeRemaining(emailDate) {
  const expiresAt = new Date(emailDate).getTime() + WINDOW_MS;
  const msLeft    = expiresAt - Date.now();
  if (msLeft <= 0) return null;
  const hLeft = msLeft / 3_600_000;
  if (hLeft < 1) return { label: `${Math.ceil(hLeft * 60)}m left`, urgent: true };
  return { label: `${Math.floor(hLeft)}h left`, urgent: hLeft < 12 };
}

function inviteAge(emailDate) {
  const ms = Date.now() - new Date(emailDate).getTime();
  const h  = ms / 3_600_000;
  if (h < 1) return `${Math.round(h * 60)}m ago`;
  if (h < 24) return `${Math.floor(h)}h ago`;
  const d = Math.floor(h / 24);
  const rem = Math.floor(h % 24);
  return rem > 0 ? `${d}d ${rem}h ago` : `${d}d ago`;
}

function extractProductName(detail) {
  if (!detail) return null;
  const m = detail.match(/(?:buy|purchase) the (.+?)\s*(?:\.|$)/i);
  return m ? m[1].trim() : null;
}

function groupByAsin(invitations) {
  const map = {};
  for (const inv of invitations) {
    const key = inv.asin || 'no-asin';
    if (!map[key]) map[key] = { asin: key, imageUrl: inv.imageUrl || null, invitations: [] };
    if (!map[key].imageUrl && inv.imageUrl) map[key].imageUrl = inv.imageUrl;
    map[key].invitations.push(inv);
  }
  return Object.values(map).sort((a, b) => b.invitations.length - a.invitations.length);
}

// Counts for a group: how many of each outcome, how many unlogged, and how many
// ordered invites are still waiting on the address that was used.
function groupOutcomeCounts(invitations) {
  const counts = {};
  let unlogged = 0;
  let missingAddress = 0;
  for (const inv of invitations) {
    const outcome = inv.log?.outcome;
    if (!outcome) { unlogged++; continue; }
    counts[outcome] = (counts[outcome] || 0) + 1;
    if (ADDRESS_OUTCOMES.includes(outcome) && !addressComplete(addressOf(inv.log))) missingAddress++;
  }
  return { counts, unlogged, missingAddress };
}

export default function InvitationsList({ invitations, isMobile, onInviteLogChanged }) {
  const [banks, setBanks] = useState([]);

  // Reloaded after each save so a newly typed bank is suggestible immediately,
  // without waiting for a page refresh.
  const loadBanks = useCallback(() => {
    fetch('/api/invitations/banks')
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setBanks(d); })
      .catch(() => {});
  }, []);

  useEffect(() => { loadBanks(); }, [loadBanks]);

  if (!invitations.length) {
    return (
      <div style={{
        textAlign: 'center', padding: '48px 0', color: '#334155', fontSize: 14,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      }}>
        <span style={{ fontSize: 32 }}>🎫</span>
        No active invitations
      </div>
    );
  }

  const groups = groupByAsin(invitations).sort((a, b) => {
    const aExpiry = Math.min(...a.invitations.map(i => new Date(i.emailDate).getTime()));
    const bExpiry = Math.min(...b.invitations.map(i => new Date(i.emailDate).getTime()));
    return aExpiry - bExpiry;
  });
  const total = invitations.length;

  return (
    <div>
      <div style={{ fontSize: 12, color: '#475569', marginBottom: 14 }}>
        {total} invitation{total !== 1 ? 's' : ''} across {groups.length} product{groups.length !== 1 ? 's' : ''}
      </div>
      <datalist id={BANK_LIST_ID}>
        {banks.map(b => <option key={b.value} value={b.value} />)}
      </datalist>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {groups.map(g => (
          <AsinCard
            key={g.asin}
            group={g}
            isMobile={isMobile}
            banks={banks}
            onBankSaved={loadBanks}
            onInviteLogChanged={onInviteLogChanged}
          />
        ))}
      </div>
    </div>
  );
}

function AsinCard({ group, isMobile, banks, onBankSaved, onInviteLogChanged }) {
  const [imgError, setImgError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const sample      = group.invitations[0];
  const productName = extractProductName(sample?.detail) || sample?.summary || '';
  const t           = TYPE_LABEL[sample?.type] || { label: sample?.type, color: '#475569', bg: '#f1f5f9' };
  const hasAsin     = group.asin !== 'no-asin';
  const imgSrc      = group.imageUrl || (hasAsin ? `https://images-na.ssl-images-amazon.com/images/P/${group.asin}.01.LZZZZZZZ.jpg` : null);
  const count       = group.invitations.length;
  const { counts, unlogged, missingAddress } = groupOutcomeCounts(group.invitations);

  const soonestDate = group.invitations.reduce(
    (min, i) => new Date(i.emailDate).getTime() < new Date(min).getTime() ? i.emailDate : min,
    group.invitations[0].emailDate
  );
  const timer = timeRemaining(soonestDate);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 12,
      overflow: 'hidden',
    }}>
      {/* Card header */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ display: 'flex', gap: isMobile ? 10 : 16, padding: isMobile ? '12px' : '14px 16px', cursor: 'pointer', alignItems: 'center' }}
      >
        {/* Product image */}
        <div style={{
          width: isMobile ? 52 : 72, height: isMobile ? 52 : 72, flexShrink: 0,
          background: 'rgba(255,255,255,0.05)', borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {imgSrc && !imgError ? (
            <img
              src={imgSrc}
              alt={productName}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              onError={() => setImgError(true)}
            />
          ) : (
            <span style={{ fontSize: 28 }}>📦</span>
          )}
        </div>

        {/* Product info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {hasAsin && (
            <div style={{ fontSize: 10, fontFamily: '"SF Mono", "Fira Code", monospace', color: '#475569', marginBottom: 3, letterSpacing: '0.04em' }}>
              {group.asin}
            </div>
          )}
          <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {productName || (hasAsin ? group.asin : 'Unknown product')}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20,
              background: t.bg, color: t.color,
            }}>
              {t.label}
            </span>
            {sample?.region && (
              <span style={{
                fontSize: 12, color: '#94a3b8',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 6, padding: '1px 7px',
              }}>
                {REGION_FLAGS[sample.region] || ''} {sample.region}
              </span>
            )}
            {timer && (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
                background: timer.urgent ? 'rgba(248,113,113,0.2)' : 'rgba(251,191,36,0.2)',
                color:      timer.urgent ? '#f87171' : '#fbbf24',
              }}>
                ⏱ {timer.label}
              </span>
            )}
            {/* Outcome summary pills */}
            {Object.entries(counts).map(([outcome, n]) => {
              const o = OUTCOMES.find(x => x.key === outcome);
              if (!o) return null;
              return (
                <span key={outcome} style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20,
                  background: o.bg, color: o.color,
                }}>
                  {n} {o.label}
                </span>
              );
            })}
            {missingAddress > 0 && (
              <span
                title="Purchased or cancelled invites with no delivery address recorded yet"
                style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20,
                  background: 'rgba(251,191,36,0.15)', color: '#fbbf24',
                  border: '1px dashed rgba(251,191,36,0.4)',
                }}
              >
                📍 {missingAddress} need address
              </span>
            )}
          </div>
        </div>

        {/* Count badge */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          background: unlogged > 0
            ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
            : 'linear-gradient(135deg, #1e293b, #334155)',
          borderRadius: 10, padding: '8px 14px',
          flexShrink: 0, minWidth: 52, textAlign: 'center',
          boxShadow: unlogged > 0 ? '0 2px 8px rgba(99,102,241,0.3)' : 'none',
        }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{count}</span>
          <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            invite{count !== 1 ? 's' : ''}
          </span>
        </div>

        <button
          onClick={async e => {
            e.stopPropagation();
            const url = `/api/invitations/export${hasAsin ? `?asin=${group.asin}` : ''}`;
            const filename = `invites_${hasAsin ? group.asin : 'all'}_${new Date().toISOString().slice(0,10)}.csv`;
            const text = await fetch(url).then(r => r.text());
            const blob = new Blob([text], { type: 'text/csv' });
            const href = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = href;
            a.setAttribute('download', filename);
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(href);
          }}
          title="Export accounts as CSV"
          style={{
            fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 7,
            background: 'rgba(255,255,255,0.06)', color: '#64748b',
            border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', flexShrink: 0,
          }}
        >
          ↓ CSV
        </button>
        <span style={{ fontSize: 12, color: '#334155', flexShrink: 0 }}>
          {expanded ? '▲' : '▼'}
        </span>
      </div>

      {/* Expanded invite list */}
      {expanded && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '4px 16px 8px', background: 'rgba(0,0,0,0.15)' }}>
          {group.invitations.map(inv => (
            <InviteRow key={inv.id} inv={inv} isMobile={isMobile} banks={banks} onBankSaved={onBankSaved} onLogChanged={onInviteLogChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

function InviteRow({ inv, isMobile, banks, onBankSaved, onLogChanged }) {
  const [saving, setSaving]         = useState(false);
  const [localLog, setLocalLog]     = useState(inv.log);
  const [draft, setDraft]           = useState(() => draftOf(inv.log));
  const [addrSaving, setAddrSaving] = useState(false);
  const [justSaved, setJustSaved]   = useState(false);
  const [addrError, setAddrError]   = useState(null);

  const tr = timeRemaining(inv.emailDate);

  // The server merges the patch and returns the whole entry, so its response is
  // what we hold on to rather than a locally reconstructed guess.
  const patchLog = useCallback(async (patch) => {
    const res  = await fetch(`/api/invitations/${inv.id}/log`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'Save failed');
    return data.log;
  }, [inv.id]);

  const setOutcome = useCallback(async (outcome) => {
    const next = localLog?.outcome === outcome ? null : outcome;
    setSaving(true);
    setAddrError(null);
    try {
      const log = await patchLog({ outcome: next });
      setLocalLog(log);
      // Clearing the outcome empties the form; changing between outcomes must
      // not discard details already typed but not yet saved.
      setDraft(d => (log ? d : draftOf(null)));
      onLogChanged?.();
    } catch {}
    setSaving(false);
  }, [localLog, patchLog, onLogChanged]);

  const saveAddress = useCallback(async () => {
    setAddrSaving(true);
    setAddrError(null);
    try {
      // The outcome rides along with every save. It is already set — the form
      // only appears once it is — but sending it means the save still lands if
      // it reaches a server process that has not seen the outcome write yet,
      // instead of being refused or, previously, silently dropped.
      const log = await patchLog({
        outcome: localLog?.outcome,
        address: {
          line1:    draft.line1.trim(),
          line2:    draft.line2.trim(),
          postcode: draft.postcode.trim().toUpperCase(),
        },
        bank: draft.bank.trim() || null,
      });
      if (!log) throw new Error('Save did not come back — nothing was changed');
      setLocalLog(log);
      // Only take the server's version if the user has not typed since the
      // request went out; otherwise their in-flight edits would be reverted.
      setDraft(d => (sameDraft(d, draft) ? draftOf(log) : d));
      onBankSaved?.();
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2200);
      onLogChanged?.();
    } catch (e) {
      setAddrError(e.message);
    }
    setAddrSaving(false);
  }, [draft, localLog?.outcome, patchLog, onLogChanged, onBankSaved]);

  const currentOutcome = localLog?.outcome || null;
  const showAddress    = ADDRESS_OUTCOMES.includes(currentOutcome);
  const stored         = draftOf(localLog);
  const dirty          = !sameDraft(draft, stored);
  const hasStored      = addressComplete(stored);

  return (
    <div style={{
      padding: '8px 0',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    }}>
      {/* Row top: email + age + time remaining */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{
          fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12,
          color: inv.accountEmail ? '#94a3b8' : '#334155',
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {inv.accountEmail || '—'}
        </span>
        <span style={{ fontSize: 11, color: '#475569', flexShrink: 0, whiteSpace: 'nowrap' }}>
          {inviteAge(inv.emailDate)}
        </span>
        {tr && (
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 10, flexShrink: 0,
            background: tr.urgent ? 'rgba(248,113,113,0.2)' : 'rgba(251,191,36,0.2)',
            color:      tr.urgent ? '#f87171' : '#fbbf24',
          }}>
            {tr.label}
          </span>
        )}
      </div>

      {/* Outcome picker */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {OUTCOMES.map(o => {
          const active = currentOutcome === o.key;
          return (
            <button
              key={o.key}
              disabled={saving}
              onClick={() => setOutcome(o.key)}
              title={o.desc}
              style={{
                fontSize: 10, fontWeight: 600,
                padding: '3px 9px', borderRadius: 20, cursor: saving ? 'wait' : 'pointer',
                border: active ? `1.5px solid ${o.color}` : '1.5px solid rgba(255,255,255,0.1)',
                background: active ? o.bg : 'transparent',
                color: active ? o.color : '#475569',
                transition: 'all 0.12s',
                opacity: saving ? 0.5 : 1,
              }}
            >
              {o.label}
            </button>
          );
        })}
        {currentOutcome && (
          <span style={{
            fontSize: 10, color: '#475569', alignSelf: 'center',
            marginLeft: 2,
          }}>
            logged {localLog?.loggedAt ? inviteAge(localLog.loggedAt) : ''}
          </span>
        )}
      </div>

      {/* Delivery address used at the time of order — kept for the address tracker */}
      {showAddress && (
        <div style={{
          marginTop: 8,
          padding: isMobile ? '10px' : '10px 12px',
          borderRadius: 8,
          background: 'rgba(255,255,255,0.03)',
          border: `1px solid ${hasStored ? 'rgba(52,211,153,0.25)' : 'rgba(251,191,36,0.3)'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>
              📍 Order details used
            </span>
            {!hasStored && (
              <span style={{ fontSize: 10, color: '#fbbf24' }}>not recorded yet</span>
            )}
            {justSaved && (
              <span style={{ fontSize: 10, color: '#34d399', fontWeight: 600 }}>✓ saved</span>
            )}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 120px',
            gap: 6,
          }}>
            <AddressField
              label="Line 1" placeholder="House no. & street"
              value={draft.line1}
              onChange={v => setDraft(d => ({ ...d, line1: v }))}
              onEnter={saveAddress}
              disabled={addrSaving}
            />
            <AddressField
              label="Line 2" placeholder="Town / area (optional)"
              value={draft.line2}
              onChange={v => setDraft(d => ({ ...d, line2: v }))}
              onEnter={saveAddress}
              disabled={addrSaving}
            />
            <AddressField
              label="Postcode" placeholder="B21 9AX"
              value={draft.postcode}
              onChange={v => setDraft(d => ({ ...d, postcode: v }))}
              onEnter={saveAddress}
              disabled={addrSaving}
              mono
            />
          </div>

          {/* Free text, but backed by a datalist of what has been entered before
              so repeat banks are one keystroke rather than retyped from memory. */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 120px',
            gap: 6, marginTop: 6,
          }}>
            <AddressField
              label="Bank used"
              placeholder={banks?.length ? `e.g. ${banks[0].value}` : 'Card / bank used to pay'}
              value={draft.bank}
              onChange={v => setDraft(d => ({ ...d, bank: v }))}
              onEnter={saveAddress}
              disabled={addrSaving}
              listId={BANK_LIST_ID}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <button
              onClick={saveAddress}
              disabled={addrSaving || !dirty}
              style={{
                fontSize: 11, fontWeight: 600, padding: '5px 14px', borderRadius: 7,
                border: '1px solid rgba(99,102,241,0.4)',
                background: dirty ? 'rgba(99,102,241,0.18)' : 'transparent',
                color: dirty ? '#a5b4fc' : '#475569',
                cursor: addrSaving ? 'wait' : dirty ? 'pointer' : 'default',
                opacity: addrSaving ? 0.6 : 1,
              }}
            >
              {addrSaving ? 'Saving…' : dirty ? 'Save details' : 'Saved'}
            </button>
            {dirty && !addressComplete(draft) && (
              <span style={{ fontSize: 10, color: '#64748b' }}>
                line 1 and postcode make it usable
              </span>
            )}
            {addrError && <span style={{ fontSize: 10, color: '#f87171' }}>{addrError}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function AddressField({ label, placeholder, value, onChange, onEnter, disabled, mono, listId }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </span>
      <input
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        list={listId}
        autoComplete="off"
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onEnter(); } }}
        style={{
          background: '#0f172a',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 6,
          color: '#e2e8f0',
          fontSize: 12,
          padding: '6px 8px',
          width: '100%',
          boxSizing: 'border-box',
          fontFamily: mono ? '"SF Mono", "Fira Code", monospace' : 'inherit',
          textTransform: mono ? 'uppercase' : 'none',
        }}
      />
    </label>
  );
}
